import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { EMPTY, catchError, finalize } from 'rxjs';

import { TwcPopupService } from '@topcon/twc-angular';

import { JobSiteService, SnapshotTypeOption, ToastService, notOnlyWhitespaces } from '@mirage-projects/cw-commons';
import { JobSiteCreationData, KeyValue } from '@mirage-projects/cw-models';

/**
 * Form to create a new site
 */
@Component({
    selector: 'cw-create-job-site-content',
    templateUrl: 'create-job-site-content.component.html',
    styleUrls: ['create-job-site-content.component.scss'],
    standalone: false,
})
export class CreateJobSiteContentComponent implements OnInit {
    createSiteForm!: FormGroup;
    defaultMaxLength = 255;
    nameRegex = '[\\s\\S]*';
    loading = false;

    private snapshotTypeOptions!: SnapshotTypeOption[];

    constructor(
        private popupService: TwcPopupService,
        private toastService: ToastService,
        private jobSiteService: JobSiteService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.snapshotTypeOptions = this.jobSiteService.snapshotTypeOptions();

        this.createForm();
    }

    /**
     * Gets the form data and call to the site creation endpoint
     * Finally, it navigates to the sites view
     */
    onCreateSite(): void {
        this.loading = true;
        const horizProjectionEPSG = this.createSiteForm.controls['horizProjectionEPSG'].value;
        const data: JobSiteCreationData = { ...this.createSiteForm.value, horizProjectionEPSG };

        this.jobSiteService
            .createJobSite(data)
            .pipe(
                finalize(() => (this.loading = false)),
                catchError(() => {
                    this.toastService.error('JOB_SITE.CREATE.ERROR');
                    return EMPTY;
                })
            )
            .subscribe(() => {
                this.closePopup();
                this.navigateToSiteView();
            });
    }

    /**
     * Set the selected coordinate reference system
     * @param value Selected CRS
     */
    setSelectedCRS(value: KeyValue): void {
        this.createSiteForm.controls['horizProjectionEPSG'].markAsDirty();
        this.createSiteForm.patchValue({
            horizProjectionEPSG: value?.key ? `EPSG:${value?.key}` : '',
        });
    }

    /**
     * Close the popup
     */
    closePopup(): void {
        const currentPopup = this.popupService.topPopup;
        this.popupService.hide(currentPopup);
    }

    private navigateToSiteView(): void {
        this.router.navigate(['/sites']);
    }

    private createForm(): void {
        this.createSiteForm = new FormGroup({
            name: new FormControl('', [Validators.required, Validators.pattern(this.nameRegex), notOnlyWhitespaces()]),
            description: new FormControl(''),
            horizProjectionEPSG: new FormControl(''),
            snapshotType: new FormControl(this.snapshotTypeOptions[0].value, Validators.required),
            sampleDistance: new FormControl(0.5, [Validators.required, Validators.min(0)]),
        });
    }
}
